interface ScrollToParameters
{
    container?: HTMLElement;
    duration?: number | ((delta: number) => number);
    extraOffset?: number;
}

export class ScrollTo
{
    public readonly container: HTMLElement;

    private readonly from: number;
    private readonly handler: FrameRequestCallback;
    private readonly delta: number;
    private readonly startTime: number;
    private readonly duration: number;

    constructor(to: HTMLElement, parameters?: ScrollToParameters)
    {
        const container = parameters?.container ?? document.documentElement;
        const elementTop = to.getBoundingClientRect().top;
        const duration = parameters?.duration;

        this.container = container;
        this.from = container.scrollTop;
        this.delta = elementTop - (parameters?.extraOffset ?? 0);
        this.duration = duration === undefined ? ScrollTo.defaultDuration(Math.abs(this.delta)) : typeof duration === "number" ? duration : duration(Math.abs(this.delta));
        this.startTime = performance.now();
        this.handler = this.process.bind(this);

        window.requestAnimationFrame(this.handler);
    }

    private static defaultDuration(delta: number): number
    {
        return delta < 100 ? 250 : delta < 500 ? 500 : delta < 1000 ? 750 : 1000;
    }

    private static easeInOutQuad(x: number): number
    {
        return x < 0.5 ? 2 * x * x : -1 - x * (2 * x - 4);
    }

    private process(): void
    {
        const percentDone = (performance.now() - this.startTime) / this.duration;
        this.container.scrollTop = this.from + this.delta * ScrollTo.easeInOutQuad(percentDone);

        if (percentDone < 1)
        {
            window.requestAnimationFrame(this.handler);
        }
    }
}

interface ScrollToHandlers
{
    onClick: EventListener;
}

export abstract class BaseService<Handlers>
{
    private readonly handlers: Handlers;

    constructor()
    {
        this.handlers = this.subscribe();
    }

    public dispose(): void
    {
        this.unsubscribe(this.handlers);
    }

    protected abstract subscribe(): Handlers;
    protected abstract unsubscribe(handlers: Handlers): void;
}

export class ScrollToService extends BaseService<ScrollToHandlers>
{
    private handleClick(event: MouseEvent): void
    {
        const sourceElement = (event.target as HTMLElement).closest("[data-logic-feature='scroll-to']") as HTMLElement | null;

        if (sourceElement === null)
        {
            return;
        }

        const targetId = sourceElement.dataset.scrollTo;

        if (targetId === undefined)
        {
            return;
        }

        const targetElement = document.getElementById(targetId);

        if (targetElement === null)
        {
            return;
        }

        event.stopImmediatePropagation();
        event.preventDefault();
        new ScrollTo(targetElement);
    }

    protected subscribe(): ScrollToHandlers
    {
        const handlers: ScrollToHandlers = {
            onClick: this.handleClick.bind(this) as EventListener
        };

        document.addEventListener("click", handlers.onClick);

        return handlers;
    }

    protected unsubscribe(handlers: ScrollToHandlers): void
    {
        document.removeEventListener("click", handlers.onClick);
    }
}